import Dropdown from '@components/Dropdown'
import PageLoader from '@components/PageLoader'
import { pluralize } from '@/utils/plural'
import { useAdminItineraryImports } from '@hooks/useAdminItineraryImports'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AdminHeading } from '../AdminLayout.styles'
import { useAdminFilter } from '../AdminFilterContext'
import ListStats from '../ListStats'
import { formatDate } from '../adminFormat'
import {
	PageButton,
	PageStatus,
	Pagination,
	ResultCount,
	Row,
	SearchInput,
	SubText,
	Table,
	TableWrap,
	Td,
	Th,
	Toolbar,
} from '../adminList.styles'
import { EmptyNote } from '@components/EmptyState'
import { ErrorNote } from '@components/Notice'
import { StatusFilterWrap, StatusPill } from './ImportsPage.styles'
import {
	ImportStatusFilter,
	importPath,
	sourceSummary,
	STATUS_FILTER_OPTIONS,
	statusLabel,
	statusTone,
} from './importView'

const PAGE_SIZE = 25

/**
 * Admin Itinerary imports list: every import across the product, searchable by trip
 * or requester, narrowable by status, server-paginated, and scoped to the global
 * creation-date range. Rows link to the read-only import detail.
 */
const ImportsPage: React.FC = () => {
	const navigate = useNavigate()
	const { from, to } = useAdminFilter()
	const [searchInput, setSearchInput] = useState('')
	const [debouncedQ, setDebouncedQ] = useState('')
	const [status, setStatus] = useState<ImportStatusFilter>('all')
	const [page, setPage] = useState(1)

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedQ(searchInput.trim()), 300)
		return () => clearTimeout(timer)
	}, [searchInput])

	useEffect(() => {
		setPage(1)
	}, [debouncedQ, status, from, to])

	const { data, isLoading, isError } = useAdminItineraryImports({
		page,
		pageSize: PAGE_SIZE,
		q: debouncedQ,
		status: status === 'all' ? undefined : status,
	})

	return (
		<>
			<AdminHeading>Itinerary imports</AdminHeading>

			<Toolbar>
				<SearchInput
					type="search"
					aria-label="Search imports by trip or requester"
					placeholder="Search by trip or requester…"
					value={searchInput}
					onChange={e => setSearchInput(e.target.value)}
				/>
				<StatusFilterWrap>
					<Dropdown
						aria-label="Import status"
						options={STATUS_FILTER_OPTIONS}
						value={status}
						onChange={setStatus}
					/>
				</StatusFilterWrap>
				{!isLoading && !isError && data && (
					<ResultCount>
						{data.total.toLocaleString()} {pluralize(data.total, 'import')}
					</ResultCount>
				)}
			</Toolbar>

			{isLoading ? (
				<PageLoader label="Loading imports…" />
			) : isError || !data ? (
				<ErrorNote>Could not load imports. Please try again.</ErrorNote>
			) : (
				<>
					<ListStats noun="imports" total={data.totalAllTime} newInRange={data.total} />

					<TableWrap>
						<Table>
							<thead>
								<tr>
									<Th>Trip</Th>
									<Th>Requested by</Th>
									<Th>Status</Th>
									<Th>Source</Th>
									<Th>Events created</Th>
									<Th>Created</Th>
								</tr>
							</thead>
							<tbody>
								{data.imports.length === 0 ? (
									<tr>
										<Td colSpan={6}>
											<EmptyNote>No imports match this filter.</EmptyNote>
										</Td>
									</tr>
								) : (
									data.imports.map(row => (
										<Row key={row.id} onClick={() => navigate(importPath(row.id))}>
											<Td>{row.tripName}</Td>
											<Td>{row.userName}</Td>
											<Td>
												<StatusPill $tone={statusTone(row.status)}>
													{statusLabel(row.status)}
												</StatusPill>
											</Td>
											<Td>{sourceSummary(row)}</Td>
											<Td>{row.createdEventCount.toLocaleString()}</Td>
											<Td>
												{formatDate(row.createdAt)}
												<SubText>{row.id.slice(0, 8)}</SubText>
											</Td>
										</Row>
									))
								)}
							</tbody>
						</Table>
					</TableWrap>

					<Pagination>
						<PageStatus>
							Page {data.page} of {data.totalPages}
						</PageStatus>
						<PageButton disabled={data.page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
							← Prev
						</PageButton>
						<PageButton disabled={data.page >= data.totalPages} onClick={() => setPage(p => p + 1)}>
							Next →
						</PageButton>
					</Pagination>
				</>
			)}
		</>
	)
}

export default ImportsPage
